import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const PortfolioContext = createContext(null);

const DEFAULT_SETTINGS = {
  membersPageVisibility: "all",
  portfolioPageVisibility: "all",
  ledgerPageVisibility: "all",
};

export function PortfolioProvider({ children }) {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [members, setMembers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [navHistory, setNavHistory] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const loadSettings = useCallback(async () => {
    try {
      const s = await api.getSettings();
      setSettings({ ...DEFAULT_SETTINGS, ...s });
    } catch (_e) {
      setSettings(DEFAULT_SETTINGS);
    }
  }, []);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setErr(null);
    try {
      const [s, m, t, n, st] = await Promise.all([
        api.portfolioSummary(),
        api.members(),
        api.transactions(),
        api.navHistory(),
        api.getSettings().catch(() => DEFAULT_SETTINGS),
      ]);
      setSummary(s);
      setMembers(m);
      setTransactions(t);
      setNavHistory(n);
      setSettings({ ...DEFAULT_SETTINGS, ...(st || {}) });
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load portfolio data");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PortfolioContext.Provider
      value={{
        summary,
        members,
        transactions,
        navHistory,
        settings,
        setSettings,
        reloadSettings: loadSettings,
        loading,
        err,
        reload: load,
      }}
    >
      {children}
    </PortfolioContext.Provider>
  );
}

export const usePortfolio = () => useContext(PortfolioContext);
